import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { getUser } from '../../utils';
import intercomReady from '../../intercom-ready';

const StyledIconButton = styled(IconButton)({
    width: 28,
    height: 28,
    borderRadius: '50%',
    overflow: 'hidden',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease, background-color 0.3s ease',
    '&:hover': {
        opacity: 0.8,
    },
});

const ImageWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
});

const CloseIconWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
});

export default () => {
    const launcherRef = useRef(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isIntercomOpen, setIsIntercomOpen] = useState(false);
    const [isIntercomReady, setIsIntercomReady] = useState(false);
    const user = getUser();

    useEffect(() => {
        if (!user) return;

        const handleShow = () => setIsIntercomOpen(true);
        const handleHide = () => setIsIntercomOpen(false);
        const handleUnreadCount = (count) => setUnreadCount(count);

        // MD-136 hotfix: intercom.js loads via dynamic import, so
        // window.Intercom may not exist yet at mount. Await the cached module
        // promise instead of silently returning (the old early-return never
        // retried, leaving the launcher unbooted).
        let cancelled = false;
        let observer = null;
        let interval = null;
        let timeout = null;

        intercomReady().then(() => {
            if (cancelled) return;

            // Wait for .intercom-button to be in DOM
            observer = new MutationObserver(() => {
                const launcher = document.querySelector('.intercom-button');
                if (launcher) {
                    observer.disconnect();

                    window.Intercom('shutdown');

                    window.Intercom('boot', {
                        app_id: 'mf8emzsc',
                        custom_launcher_selector: '.intercom-button',
                        name: user.name,
                        email: user.email,
                        user_id: user.id,
                        Region: user.region,
                        'Account Type': user.role.charAt(0).toUpperCase() + user.role.slice(1),
                        hide_default_launcher: true,
                        vertical_padding: 100,
                    });

                    window.Intercom('onShow', handleShow);
                    window.Intercom('onHide', handleHide);
                    window.Intercom('onUnreadCountChange', handleUnreadCount);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            interval = setInterval(() => {
                if (window.Intercom?.booted) {
                    setIsIntercomReady(true);
                    clearInterval(interval);
                    clearTimeout(timeout);
                }
            }, 100);
            timeout = setTimeout(() => {
                clearInterval(interval);
            }, 30000);
        });

        return () => {
            cancelled = true;
            if (observer) observer.disconnect();
            if (interval) clearInterval(interval);
            if (timeout) clearTimeout(timeout);
            if (window.Intercom) {
                window.Intercom('off', 'onShow', handleShow);
                window.Intercom('off', 'onHide', handleHide);
                window.Intercom('off', 'onUnreadCountChange', handleUnreadCount);
            }
        };
    }, [user]);

    if (!user) return null;

    return (
        <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
                '& .MuiBadge-badge': {
                    right: 4,
                    top: 4,
                },
            }}
        >
            <StyledIconButton
                ref={launcherRef}
                className="intercom-button"
                sx={{
                    opacity: isIntercomReady ? 1 : 0,
                    pointerEvents: isIntercomReady ? 'auto' : 'none',
                    backgroundColor: isIntercomOpen ? '#0071ab' : 'transparent',
                    '&:hover': {
                        backgroundColor: isIntercomOpen ? '#0071ab' : 'transparent',
                    },
                }}
            >
                <ImageWrapper
                    sx={{
                        opacity: isIntercomOpen ? 0 : 1,
                        transform: isIntercomOpen ? 'scale(0.8)' : 'scale(1)',
                        position: 'absolute',
                    }}
                >
                    <img
                        src="https://milkmoovement-public-assets.s3.ca-central-1.amazonaws.com/383f2ac96dfbc0bb3415e0ab1e951086.png"
                        alt="Button icon"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </ImageWrapper>
                <CloseIconWrapper
                    sx={{
                        opacity: isIntercomOpen ? 1 : 0,
                        transform: isIntercomOpen ? 'scale(1)' : 'scale(0.8)',
                        position: 'absolute',
                    }}
                >
                    <CloseIcon sx={{ fontSize: 24, color: 'white' }} />
                </CloseIconWrapper>
            </StyledIconButton>
        </Badge>
    );
};
